import React, { useState, useEffect, useCallback, useMemo } from 'react'; // Added useMemo here too for safety
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Icon,
  useColorModeValue,
  Tooltip,
  Wrap,
  WrapItem,
  Divider,
  Tag,
  TagLabel,
  TagLeftIcon,
  Flex,
  Spacer
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  Calendar,
  MapTrifold,
  Star,
  MagicWand,
  User,
  ListPlus,
  Info
} from '@phosphor-icons/react';
import { normalizeFeature, formatFeatureName } from '../../utils/featureUtils';
import SearchBetaImagePreview from './SearchBetaImagePreview';
import ImagePreviewModal from '../../components/ImagePreviewModal';
import SourceLinkedText from '../site/SourceLinkedText'; // Added import

const SearchBetaSiteCard = ({
  site,
  activeFeatures = [],
  onFeatureFilter,
  groundingMetadata // Added groundingMetadata prop
}) => {
  console.log('[SearchBetaSiteCard] Received site prop:', site); // <-- ADDED LOG
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  // Log the site prop when the component renders
  console.log(`[SearchBetaSiteCard] Rendering card for site: ${site?.id}`, site);

  // Ancient map aesthetic colors
  const accentColor = useColorModeValue('ancient.terracotta', 'ancient.gold');
  const textColor = useColorModeValue('ancient.ink', 'ancient.ink.light');
  const subtleTextColor = useColorModeValue('ancient.ink.subtle', 'ancient.gold.subtle');

  // Feature badge colors
  const defaultBadgeBg = useColorModeValue('ancient.subtle', 'gray.700');
  const selectedBadgeBg = useColorModeValue('ancient.terracotta.100', 'ancient.terracotta.900');
  const badgeTextColor = useColorModeValue('ancient.terracotta.800', 'ancient.terracotta.100');

  // Source type colors for feature badges
  const sourceBadgeColors = {
    system: useColorModeValue('blue.100', 'blue.800'),
    auto: useColorModeValue('purple.100', 'purple.800'),
    manual: useColorModeValue('green.100', 'green.800')
  };

  const handleCardClick = (e) => {
    if (e.target.closest('.feature-badge') || e.target.closest('.image-preview-trigger')) return; // Prevent nav on badge or image click
    navigate(`/site/${site.id}`);
  };

  // Simplified image handling: Primarily use mainImageUrl and its predictions
  // Assumes `sitesearch_beta` provides `mainImageUrl` and `mainImagePredictions`
  // If `mediaItems` is denormalized in the future, this can be expanded.
  const allSiteImages = useMemo(() => {
    if (!site.mainImageUrl) {
      console.log(`[SearchBetaSiteCard] No mainImageUrl for site ${site?.id}`);
      return [];
    }

    const mainImage = {
      id: `main-${site.id}`, // Use a consistent ID format
      url: site.mainImageUrl,
      predictions: site.mainImagePredictions || [], // Use predictions from root if available
      type: 'image',
      siteId: site.id
    };

    console.log(`[SearchBetaSiteCard] Using main image for site ${site?.id}:`, [mainImage]);
    return [mainImage]; // Return only the main image for now
  }, [site.id, site.mainImageUrl, site.mainImagePredictions]);

  // Find the index of the clicked image and open the modal (will always be 0 if main image exists)
  const handleImageClick = useCallback((clickedImage) => {
    // Since we only have the main image now, the index will be 0 if it exists
    if (allSiteImages.length > 0 && clickedImage.url === allSiteImages[0].url) {
      setSelectedImageIndex(0);
      setIsPreviewOpen(true);
    }
  }, [allSiteImages]);

  // Handlers for modal navigation (disabled if only one image)
  const handlePreviousImage = useCallback(() => {
    // No-op if only one image
  }, []);

  const handleNextImage = useCallback(() => {
    // No-op if only one image
  }, []);

  const handleFeatureClick = (e, feature) => {
    e.stopPropagation();
    if (onFeatureFilter) {
      onFeatureFilter(feature);
    }
  };

  // Check if a feature is in the active filters
  const isFeatureSelected = (featureId) => {
    if (!activeFeatures || activeFeatures.length === 0) return false;
    const normalizedId = normalizeFeature(featureId);
    return activeFeatures.some(id => normalizeFeature(id) === normalizedId);
  };

  // Removed getFeatureSourceType and getFeatureSourceIcon as source info is unavailable

  // Use only features_normalized for display
  const getAllFeatures = useMemo(() => {
    const baseFeatures = site.features_normalized || [];
    // Map to the structure expected by the rendering logic (id only)
    return baseFeatures.map(featureId => ({ id: featureId }));
  }, [site.features_normalized]);


  return (
    <>
      <Box
        w="100%"
        bg="ancient.papyrus"
        borderRadius="lg"
        border="1px"
        borderColor="ancient.mapBorder"
        overflow="hidden"
        boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
        transition="all 0.3s ease"
        cursor="pointer"
        onClick={handleCardClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="map-texture"
        position="relative"
        _hover={{
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          borderColor: accentColor,
        }}
      >
        {/* Image Section - Use combined list */}
        <Box height="250px" className="image-preview-trigger"> {/* Added class to help stop propagation */}
          {allSiteImages.length > 0 ? (
            <SearchBetaImagePreview
              images={allSiteImages} // Pass the combined list
              activeFeatures={activeFeatures}
              onImageClick={handleImageClick} // This now opens the modal
              alt={`${site.name} - Featured image`}
            />
          ) : (
            <Box
              height="100%"
              width="100%"
              bg="ancient.lightStone"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
            >
              <Icon as={MapTrifold} color="ancient.terracotta" boxSize={10} mb={2} />
              <Text color="ancient.ink" fontSize="sm">No images available</Text>
            </Box>
          )}
        </Box>

        {/* Content Section */}
        <Box p={5}>
          <VStack align="stretch" spacing={4}>
            {/* Title */}
            <VStack align="start" spacing={2}>
              <Heading
                size="md"
                color={textColor}
                noOfLines={1}
                fontFamily="'Cormorant Garamond', serif"
                fontWeight="600"
                letterSpacing="0.5px"
                position="relative"
                _after={{
                  content: '""',
                  position: 'absolute',
                  bottom: '-2px',
                  left: 0,
                  width: '40%',
                  height: '1px',
                  bg: accentColor,
                  backgroundImage: useColorModeValue(
                    "linear-gradient(90deg, var(--chakra-colors-ancient-terracotta), transparent)",
                    "linear-gradient(90deg, var(--chakra-colors-ancient-gold), transparent)"
                  )
                }}
              >
                {site.name}
              </Heading>
            </VStack>

            {/* Location and Era Info */}
            <VStack align="stretch" spacing={2.5}>
              <VStack align="start" spacing={1}>
                <HStack>
                  <Icon as={MapPin} weight="fill" color={accentColor} boxSize={4} />
                  <Text
                    color={textColor}
                    noOfLines={1}
                    fontWeight="500"
                    fontFamily="'Cormorant Garamond', serif"
                  >
                    {
                      (site.location?.region && site.location?.country)
                        ? `${site.location.region}, ${site.location.country}`
                        : site.location?.country || site.location?.region || site.location?.address || 'Unknown location'
                    }
                  </Text>
                </HStack>

                <HStack>
                  <Icon as={Calendar} weight="regular" color={subtleTextColor} boxSize={4} />
                  <Text
                    color={subtleTextColor}
                    fontFamily="'Cormorant Garamond', serif"
                  >
                    {site.startEra && site.endEra ?
                      `${site.startEra} - ${site.endEra}` :
                      site.startEra || site.endEra || 'Era unknown'}
                    {site.culture ? ` (${site.culture})` : ''}
                  </Text>
                </HStack>

                <HStack>
                  <Icon as={MapTrifold} weight="regular" color={subtleTextColor} boxSize={4} />
                  <Text
                    color={subtleTextColor}
                    fontSize="sm"
                    fontFamily="'Cormorant Garamond', serif"
                    noOfLines={1}
                  >
                    {site.location?.coordinates ?
                      `${site.location.coordinates.latitude?.toFixed(6)}, ${site.location.coordinates.longitude?.toFixed(6)}` : // Added optional chaining
                      'Coordinates unavailable'}
                  </Text>
                </HStack>
              </VStack>
            </VStack>

            {/* Description */}
            {site.description && (
              <>
                <Divider />
                {/* Use SourceLinkedText for description */}
                <Box pl={3} borderLeftWidth="2px" borderColor={accentColor} >
                  <SourceLinkedText
                    text={site.description}
                    groundingMetadata={groundingMetadata}
                    citations={site.citations || []} // Pass manual citations
                    noOfLines={2} // Apply styling props here if needed
                  />
                </Box>
              </>
            )}

            {/* Features Section */}
            {getAllFeatures.length > 0 && (
              <>
                <Divider />
                <Box>
                  <HStack mb={2} justify="space-between" align="center">
                    <Text
                      fontWeight="600"
                      fontSize="sm"
                      color={textColor}
                      fontFamily="'Cormorant Garamond', serif"
                      letterSpacing="0.5px"
                    >
                      Features:
                    </Text>
                    {activeFeatures && activeFeatures.length > 0 && (
                      <Text
                        fontSize="xs"
                        color={subtleTextColor}
                        fontStyle="italic"
                      >
                        {activeFeatures.length} filter{activeFeatures.length !== 1 ? 's' : ''} active
                      </Text>
                    )}
                  </HStack>
                  <Wrap spacing={2}>
                    {getAllFeatures.map(feature => {
                      const featureId = feature.id;
                      const normalizedId = normalizeFeature(featureId);
                      const isSelected = isFeatureSelected(normalizedId);
                      // Removed source type logic
                      const displayName = formatFeatureName(featureId);

                      // Removed confidence logic

                      return (
                        <WrapItem key={normalizedId} className="feature-badge">
                          <Tooltip
                            label={
                              <Box>
                                <Text fontWeight="bold">{displayName}</Text>
                                {/* Removed source/confidence info */}
                                <Text fontSize="xs" mt={1}>
                                  {isSelected ? 'Click to remove filter' : 'Click to add filter'}
                                </Text>
                              </Box>
                            }
                            placement="top"
                            hasArrow
                          >
                            <Tag
                              size="md"
                              variant={isSelected ? "solid" : "subtle"}
                              colorScheme={isSelected ? "ancient" : "gray"}
                              bg={isSelected ? selectedBadgeBg : defaultBadgeBg}
                              color={isSelected ? badgeTextColor : 'ancient.terracotta'}
                              cursor="pointer"
                              _hover={{
                                transform: 'translateY(-2px)',
                                boxShadow: 'md',
                                bg: isSelected ? selectedBadgeBg : 'ancient.lightStone' // Simplified hover bg
                              }}
                              transition="all 0.2s ease"
                              borderRadius="full"
                              boxShadow={isSelected ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}
                              onClick={(e) => handleFeatureClick(e, { id: featureId, label: displayName })}
                            >
                              {/* Removed TagLeftIcon */}
                              <TagLabel>
                                {displayName}
                                {/* Removed confidence checkmark */}
                              </TagLabel>
                            </Tag>
                          </Tooltip>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                </Box>
              </>
            )}
          </VStack>
        </Box>
      </Box>

      {/* Image Preview Modal - Use combined list */}
      {selectedImageIndex !== null && (
        <ImagePreviewModal
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setSelectedImageIndex(null); // Reset index on close
          }}
          imageUrl={allSiteImages?.[selectedImageIndex]?.url} // Use combined list
          currentImage={allSiteImages?.[selectedImageIndex]} // Use combined list
          hasMultiple={allSiteImages.length > 1} // Use combined list length
          onPrevious={handlePreviousImage}
          onNext={handleNextImage}
          isReadOnly={true} // Add this prop
          // Pass other necessary props if ImagePreviewModal needs them, e.g., onFeaturesUpdated
        />
      )}
    </>
  );
};

export default SearchBetaSiteCard;
